import React from "react";
import toast from "react-hot-toast";
import { getApiUrl } from "./config";
import authService from "./AuthService";
import "./HalStatusView.css";
import { AdoptableSlot, DiscoveredModule, SupportedModule, adopt, adoptableSlots, usedIds } from "./Adopt";
import { Description } from "./Endpoint";

// State of one I/O interface of a provider, as returned by /v1/endpoint/{id}/hal/io.
type HalInterface = {
  name: string;
  friendlyName?: string;
  mode?: string;
  value: boolean | number | null; // null: a serial port (MODBUS_RTU, P1), which only has a mode
};

// State of an I/O provider: the HAL (wems.hal) or an I/O appliance.
type HalIOProvider = {
  id: string;
  friendlyName?: string;
  state?: string;
  interfaces: Array<HalInterface>;
  error?: string;
  // DYNAMIC providers only (e.g. a coupler): the discovered stack.
  layout?: string;
  layoutHash?: string;
  modules?: Array<DiscoveredModule>;
};

// An adoption in progress: the slots without a module id and the article chosen for each.
type Adoption = {
  provider: HalIOProvider;
  description: Description;
  slots: Array<AdoptableSlot>;
  chosen: Map<number, string>;
};

type HalIOStatus = {
  providers: Array<HalIOProvider>;
};

const platformId = "wems.hal";

// The unit of an analog or temperature value, from the interface mode.
function unit(mode?: string): string {
  if (mode === undefined) return "";
  if (mode.endsWith("_V")) return "V";
  if (mode.endsWith("_MA")) return "mA";
  if (mode.endsWith("_KOHM")) return "kΩ";
  if (mode.startsWith("PT") || mode.startsWith("NI")) return "K";
  return "";
}

type HalStatusViewProperties = {
  endpointId: string;
};

type HalStatusViewState = {
  status?: HalIOStatus;
  loading: boolean;
  edited?: { provider: HalIOProvider; output: HalInterface; newValue: number };
  adoption?: Adoption;
};

// HalStatusView shows the I/O of every provider of an endpoint. The status is only requested from the endpoint on
// demand, to not load the controller each time the page is opened.
class HalStatusView extends React.Component<HalStatusViewProperties, HalStatusViewState> {
  constructor(props: HalStatusViewProperties) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  private fetchStatus = () => {
    this.setState({ loading: true });
    fetch(getApiUrl() + "/v1/endpoint/" + this.props.endpointId + "/hal/io", {
      method: "GET",
      headers: [
        ["Accept", "application/json"],
        ["Authorization", authService.authHeader()],
      ],
    })
      .then(async (response) => {
        if (response.ok) {
          this.setState({ status: (await response.json()) as HalIOStatus });
        } else if (response.status === 503) {
          toast.error("Endpoint is offline");
        } else if (response.status === 408) {
          toast.error("The endpoint did not send its HAL status");
        } else {
          toast.error("Failed to get HAL status (" + response.status + ")");
        }
      })
      .catch(() => toast.error("Failed to get HAL status"))
      .finally(() => this.setState({ loading: false }));
  };

  private sendCommand(body: object, description: string, applied: () => void) {
    fetch(getApiUrl() + "/v1/endpoint/" + this.props.endpointId + "/hal/io", {
      method: "POST",
      headers: [
        ["Content-Type", "application/json"],
        ["Authorization", authService.authHeader()],
      ],
      body: JSON.stringify({ publishStatus: false, ...body }),
    })
      .then((response) => {
        if (response.ok) {
          applied();
        } else {
          toast.error("Failed to " + description + " (" + response.status + ")");
        }
      })
      .catch(() => toast.error("Failed to " + description));
  }

  // setValue updates the shown value of an interface after a command was sent.
  private setValue(provider: HalIOProvider, name: string, value: boolean | number) {
    if (!this.state.status) return;
    this.setState({
      status: {
        providers: this.state.status.providers.map((p) =>
          p.id !== provider.id
            ? p
            : { ...p, interfaces: p.interfaces.map((i) => (i.name === name ? { ...i, value: value } : i)) }
        ),
      },
    });
  }

  private toggleDigitalOutput(provider: HalIOProvider, output: HalInterface) {
    const state = !output.value;
    this.sendCommand(
      { doCmds: [{ provider: provider.id, name: output.name, state: state }] },
      "switch " + output.name,
      () => this.setValue(provider, output.name, state)
    );
  }

  private setAnalogOutput = () => {
    const { provider, output, newValue } = this.state.edited!;
    if (isNaN(newValue)) {
      toast.error("Invalid value for " + output.name);
      return;
    }
    this.setState({ edited: undefined });
    this.sendCommand(
      { aoCmds: [{ provider: provider.id, name: output.name, value: newValue }] },
      "set " + output.name,
      () => this.setValue(provider, output.name, newValue)
    );
  };

  private async getJson<T>(path: string): Promise<T> {
    const response = await fetch(getApiUrl() + path, {
      method: "GET",
      headers: [
        ["Accept", "application/json"],
        ["Authorization", authService.authHeader()],
      ],
    });
    if (!response.ok) {
      throw new Error(path + ": " + response.status);
    }
    return (await response.json()) as T;
  }

  // startAdopt reads the endpoint description and the provider's supportedModules, and opens the adopt dialog.
  private startAdopt = async (provider: HalIOProvider) => {
    try {
      const description = await this.getJson<Description>("/v1/endpoint/" + this.props.endpointId + "/description?includeApplianceConfiguration=true");
      const appliance = (description.processes ?? []).flatMap((p) => p.appliances).find((a) => a.id === provider.id);
      if (!appliance) {
        toast.error(provider.id + " is not in the endpoint description");
        return;
      }
      const component = await this.getJson<{ supportedModules?: Array<SupportedModule> }>(
        "/v1/component/appliance/reference/" + appliance.component + "/version/" + (appliance.version || "latest")
      );
      const configuration = (appliance.configuration ?? {}) as unknown as Record<string, string>;
      const slots = adoptableSlots(provider.modules ?? [], configuration, component.supportedModules ?? []);
      const chosen = new Map<number, string>();
      slots.forEach((s) => s.candidates.length > 0 && chosen.set(s.slot, s.candidates[0].article));
      this.setState({ adoption: { provider, description, slots, chosen } });
    } catch (error) {
      toast.error("Failed to prepare the adoption: " + error);
    }
  };

  // applyAdopt writes the new modules into the provider's configuration and publishes the endpoint description.
  private applyAdopt = () => {
    const { provider, description, slots, chosen } = this.state.adoption!;
    const appliances = (description.processes ?? []).flatMap((p) => p.appliances);
    const appliance = appliances.find((a) => a.id === provider.id)!;
    const configuration = (appliance.configuration ?? {}) as unknown as Record<string, string>;
    const others = appliances.filter((a) => a.id !== provider.id).map((a) => (a.configuration ?? {}) as unknown as Record<string, string>);
    const busIds = new Map(slots.map((s) => [s.slot, s.busId]));
    appliance.configuration = adopt(configuration, chosen, busIds, usedIds(provider.id, configuration, others)) as unknown as Map<string, string>;
    this.setState({ adoption: undefined });

    fetch(getApiUrl() + "/v1/endpoint/" + this.props.endpointId + "/description", {
      method: "PUT",
      headers: [
        ["Accept", "application/json"],
        ["Content-Type", "application/json"],
        ["Authorization", authService.authHeader()],
      ],
      body: JSON.stringify(description),
    })
      .then((response) => {
        if (response.ok) {
          toast.success("Modules adopted, the I/O appliance restarts with the new plan");
        } else {
          toast.error("Failed to update the endpoint description (" + response.status + ")");
        }
      })
      .catch(() => toast.error("Failed to update the endpoint description"));
  };

  private renderAdoption() {
    const adoption = this.state.adoption!;
    return (
      <div className="LiveEndpointView-editor-overlay">
        <div className="LiveEndpointView-editor hal-adopt">
          <span className="LiveEndpointView-editorTitle">Adopt the modules of {adoption.provider.friendlyName || adoption.provider.id}</span>
          {adoption.slots.map((s) => (
            <div className="adopt-slot" key={s.slot}>
              <span>
                Slot {s.slot} ({s.busId})
              </span>
              {s.candidates.length === 0 ? (
                <span className="unsupported">not supported, left out</span>
              ) : (
                <select
                  value={adoption.chosen.get(s.slot)}
                  onChange={(event) => {
                    const chosen = new Map(adoption.chosen);
                    chosen.set(s.slot, event.target.value);
                    this.setState({ adoption: { ...adoption, chosen } });
                  }}
                >
                  {s.candidates.map((c) => (
                    <option key={c.article} value={c.article}>
                      {c.article} {c.friendlyName}
                    </option>
                  ))}
                </select>
              )}
              {s.replaces && s.candidates.length > 0 && <small>replaces {s.replaces}: its references stop working</small>}
            </div>
          ))}
          <div className="LiveEndpointView-buttonBox">
            <button onClick={this.applyAdopt} disabled={adoption.chosen.size === 0}>
              Adopt
            </button>
            <button onClick={() => this.setState({ adoption: undefined })}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  private renderModules(provider: HalIOProvider) {
    const modules = provider.modules ?? [];
    return (
      <div className="module-list">
        {modules.length === 0 && <div className="noData">No modules discovered</div>}
        {modules.map((m) => (
          <div className={"module" + (m.id ? "" : " unplanned")} key={m.slot}>
            <span className="slot">Slot {m.slot}</span>
            <span className="bus-id">{m.busId}</span>
            <span>{m.id ? m.article + " · " + m.id + (m.friendlyName ? " (" + m.friendlyName + ")" : "") : "not planned or not matching the plan"}</span>
          </div>
        ))}
        {modules.some((m) => !m.id) && (
          <div className="hal-status-actions">
            <button onClick={() => this.startAdopt(provider)}>Adopt</button>
          </div>
        )}
      </div>
    );
  }

  private renderValue(provider: HalIOProvider, i: HalInterface) {
    if (i.value === null) {
      return <span className="value">-</span>;
    }
    if (typeof i.value === "boolean") {
      if (i.mode === "DIGITAL_OUTPUT") {
        return (
          <span className={"value binary writable" + (i.value ? " active" : "")} onClick={() => this.toggleDigitalOutput(provider, i)}>
            {i.value ? "On" : "Off"}
          </span>
        );
      }
      return <span className={"value binary" + (i.value ? " active" : "")}>{i.value ? "On" : "Off"}</span>;
    }

    const value = i.value;
    const formatted = (
      <>
        {value.toFixed(2)} <span className="unit"> [{unit(i.mode) || " "}] </span>
      </>
    );
    if (i.mode?.startsWith("ANALOG_OUTPUT")) {
      return (
        <span className="value writable" onClick={() => this.setState({ edited: { provider: provider, output: i, newValue: value } })}>
          {formatted}
        </span>
      );
    }
    return <span className="value">{formatted}</span>;
  }

  // renderEditor is the analog output editor, the same as the numeric set point editor of the live data.
  private renderEditor() {
    const edited = this.state.edited!;
    return (
      <div className="LiveEndpointView-editor-overlay">
        <div className="LiveEndpointView-editor">
          <span className="LiveEndpointView-editorTitle">{edited.output.name}</span>
          <input
            type="number"
            step={0.01}
            value={edited.newValue}
            onChange={(event) => this.setState({ edited: { ...edited, newValue: parseFloat(event.target.value) } })}
          />
          <div className="LiveEndpointView-buttonBox">
            <button onClick={this.setAnalogOutput}>Ok</button>
            <button onClick={() => this.setState({ edited: undefined })}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  private renderProvider(provider: HalIOProvider) {
    const name = provider.id === platformId ? "Platform HAL (" + platformId + ")" : provider.friendlyName || provider.id;
    const state = provider.state ?? (provider.error ? "FAILED" : "RUNNING");
    return (
      <div className="hal-provider" key={provider.id}>
        <h4 className={state}>
          {name}
          {state !== "RUNNING" && <small className="provider-state">({state})</small>}
        </h4>
        {provider.error && <div className="provider-error">{provider.error}</div>}
        {provider.layout === "DYNAMIC" && this.renderModules(provider)}
        {provider.interfaces.length > 0 ? (
          <div className="interface-list">
            {provider.interfaces.map((i) => (
              <div className="interface" key={i.name}>
                <span className="name">
                  {i.name}
                  {i.friendlyName && i.friendlyName !== i.name && <small> ({i.friendlyName})</small>}
                </span>
                <span className="mode">{i.mode ?? "-"}</span>
                {this.renderValue(provider, i)}
              </div>
            ))}
          </div>
        ) : (
          !provider.error && provider.layout !== "DYNAMIC" && <div className="noData">No interfaces</div>
        )}
      </div>
    );
  }

  render() {
    return (
      <div className="hal-status">
        <div className="hal-status-actions">
          <button onClick={this.fetchStatus} disabled={this.state.loading}>
            {this.state.loading ? "Requesting…" : this.state.status ? "Refresh HAL status" : "Get HAL status"}
          </button>
        </div>
        {this.state.status && this.state.status.providers.map((provider) => this.renderProvider(provider))}
        {this.state.edited && this.renderEditor()}
        {this.state.adoption && this.renderAdoption()}
      </div>
    );
  }
}

export default HalStatusView;
